import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Star, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * Reusable multi-bank-account editor for Customer / Supplier / Partner / Employee.
 * Props:
 *   value: array of {account_name, bank_name, account_number, iban, is_default}
 *   onChange: (newArray) => void
 *   testidPrefix: string for data-testid attributes (e.g. "cust-bank")
 */
export default function BankAccountsEditor({ value = [], onChange, testidPrefix = 'bank-acc' }) {
  const accounts = Array.isArray(value) ? value : [];
  const [validating, setValidating] = useState({});

  const update = (idx, patch) => {
    const next = accounts.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    if (patch.is_default) {
      // Single default: clear others
      next.forEach((a, i) => { if (i !== idx) a.is_default = false; });
    }
    onChange(next);
  };
  const add = () => onChange([...accounts, { account_name: '', bank_name: '', account_number: '', iban: '', is_default: accounts.length === 0 }]);
  const remove = (idx) => onChange(accounts.filter((_, i) => i !== idx));

  const validateIban = async (idx, iban) => {
    if (!iban) return;
    setValidating(v => ({ ...v, [idx]: true }));
    try {
      const r = await api.post('/bank-matching/validate-iban', { iban });
      update(idx, { iban_valid: r.data.valid, iban_country: r.data.country, iban_reason: r.data.reason });
    } catch { /* ignore */ }
    finally { setValidating(v => ({ ...v, [idx]: false })); }
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Bank Accounts</Label>
        <Button type="button" variant="ghost" size="sm" onClick={add} className="h-7 text-xs" data-testid={`${testidPrefix}-add`}>
          <Plus size={12} className="mr-1" /> Add account
        </Button>
      </div>
      {accounts.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic px-1">No bank accounts on file. Add one to enable smart-matching from bank statements.</p>
      ) : (
        <div className="space-y-2">
          {accounts.map((acct, idx) => (
            <div key={idx} className="rounded-lg border border-border p-2 grid grid-cols-1 sm:grid-cols-12 gap-2 bg-muted/30" data-testid={`${testidPrefix}-row-${idx}`}>
              <div className="sm:col-span-3">
                <Input placeholder="Account name (label)" value={acct.account_name || ''}
                  onChange={(e) => update(idx, { account_name: e.target.value })}
                  className="h-8 text-xs" data-testid={`${testidPrefix}-name-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Input placeholder="Bank name" value={acct.bank_name || ''}
                  onChange={(e) => update(idx, { bank_name: e.target.value })}
                  className="h-8 text-xs" data-testid={`${testidPrefix}-bank-${idx}`} />
              </div>
              <div className="sm:col-span-2">
                <Input placeholder="Account #" value={acct.account_number || ''}
                  onChange={(e) => update(idx, { account_number: e.target.value })}
                  className="h-8 text-xs font-mono" data-testid={`${testidPrefix}-num-${idx}`} />
              </div>
              <div className="sm:col-span-3">
                <Input placeholder="IBAN" value={acct.iban || ''}
                  onChange={(e) => update(idx, { iban: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                  onBlur={(e) => validateIban(idx, e.target.value)}
                  className={`h-8 text-xs font-mono ${acct.iban_valid === false ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
                  data-testid={`${testidPrefix}-iban-${idx}`} />
                {acct.iban_valid === false && (
                  <p className="text-[10px] text-rose-600 mt-0.5 inline-flex items-center gap-1" data-testid={`${testidPrefix}-iban-err-${idx}`}>
                    <AlertTriangle size={10} /> {acct.iban_reason || 'Invalid IBAN'}
                  </p>
                )}
                {acct.iban_valid === true && acct.iban_country && (
                  <p className="text-[10px] text-emerald-600 mt-0.5">✓ {acct.iban_country}</p>
                )}
              </div>
              <div className="sm:col-span-1 flex items-center justify-end gap-1">
                <button type="button"
                  onClick={() => update(idx, { is_default: !acct.is_default })}
                  title={acct.is_default ? 'Default account' : 'Set as default'}
                  className={`h-7 w-7 rounded flex items-center justify-center ${acct.is_default ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'hover:bg-muted text-muted-foreground'}`}
                  data-testid={`${testidPrefix}-default-${idx}`}>
                  <Star size={12} className={acct.is_default ? 'fill-current' : ''} />
                </button>
                <button type="button" onClick={() => remove(idx)} title="Remove"
                  className="h-7 w-7 rounded flex items-center justify-center text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/30"
                  data-testid={`${testidPrefix}-remove-${idx}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              {validating[idx] && <p className="text-[10px] text-muted-foreground sm:col-span-12">Validating IBAN…</p>}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground px-1">
        Tip: IBAN-exact matches enable HIGH-confidence auto-matching from bank statements (Settings → Bank Matching).
      </p>
    </div>
  );
}
